import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import { ThemeProvider } from './contexts/ThemeContext';
import { supabase } from './supabaseClient';
import './App.css';
import './AdminPanel.css';
import Header from './components/Header';
import Homepage from './components/Homepage';
import About from './components/About';
import Contact from './components/Contact';
import ProductCatalog from './components/ProductCatalog';
import OrderPlacement from './components/OrderPlacement';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import Login from './components/Login';
import Signup from './components/Signup';
import KitDesigner3D from './pages/KitDesigner3D';

function App() {
  const [currentView, setCurrentView] = useState('home');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [authView, setAuthView] = useState('login'); // 'login' or 'signup'
  const [customization] = useState({
    baseColor: '#FF0000',
    accentColor: '#FFFFFF',
    sleevesColor: '#000000',
    teamName: '',
    playerName: '',
    playerNumber: '',
    sponsorLogo: null,
    view: 'product', // 'product' or '3d'
    // Size data for each element
    sizes: {
      teamName: 24,
      playerName: 28,
      playerNumber: 80,
      logo: 100
    },
    // Position data for draggable elements (2D view - legacy)
    positions: {
      back: {
        name: { x: 50, y: 25 },
        number: { x: 50, y: 50 },
        logo: { x: 50, y: 10 },
        teamName: { x: 50, y: 15 }
      },
      front: {
        teamName: { x: 50, y: 60 },
        logo: { x: 50, y: 20 }
      }
    },
    // Front positions for 3D view
    frontPositions: {
      teamName: { x: 50, y: 30 },
      logo: { x: 50, y: 15 }
    }
  });
  const [showOrderPlacement, setShowOrderPlacement] = useState(false);
  const [kitDesignData, setKitDesignData] = useState(null);

  // Handle Stripe payment success redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      window.history.replaceState({}, '', window.location.pathname);

      const sendEmails = async () => {
        const pending = localStorage.getItem('pendingOrderEmail');
        if (!pending) return;
        try {
          const { fullName, email, orderNum, message } = JSON.parse(pending);
          const emailParams = { from_name: fullName, order_number: orderNum, message };

          await emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
            { ...emailParams, to_email: 'universalsportslab@gmail.com' },
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
          );

          await emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
            {
              ...emailParams,
              to_email: email,
              message: `Hi ${fullName},\n\nYour payment was successful! Here are your order details:\n\n${message}\n\nWe will process your order and be in touch soon.\n\n— Universal Sports Lab`,
            },
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
          );

          localStorage.removeItem('pendingOrderEmail');
          console.log('Confirmation emails sent successfully');
        } catch (e) {
          console.error('Post-payment email failed:', e);
        }
      };

      sendEmails();
    }
  }, []);

  // Check if admin is logged in on mount
  useEffect(() => {
    const adminStatus = localStorage.getItem('adminLoggedIn');
    if (adminStatus === 'true') {
      setIsAdminLoggedIn(true);
    }
  }, []);

  // Check for existing user session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
    setShowOrderPlacement(true);
    setCurrentView('customize');
  };

  const handleAdminLogin = (status) => {
    setIsAdminLoggedIn(status);
    if (status) {
      setCurrentView('admin');
    }
  };

  const handleNavigation = (view) => {
    if (view === 'admin') {
      // Check if logged in, otherwise show login
      if (!isAdminLoggedIn) {
        setCurrentView('admin-login');
      } else {
        setCurrentView('admin');
      }
    } else if (view === 'auth') {
      setCurrentView('auth');
      setAuthView('login');
    } else {
      setCurrentView(view);
      if (view === 'home') {
        setSelectedProduct(null);
        setShowOrderPlacement(false);
      }
    }
  };

  const handleUserLogin = (userData) => {
    setUser(userData);
    setCurrentView('home');
  };

  const handleUserLogout = () => {
    setUser(null);
    setCurrentView('home');
  };

  const handleExploreProducts = () => {
    setCurrentView('products');
  };

  const renderCurrentView = () => {
    // User Auth Routes
    if (currentView === 'auth') {
      if (authView === 'login') {
        return (
          <Login 
            onSuccess={handleUserLogin}
            onSwitchToSignup={() => setAuthView('signup')}
            onBack={() => setCurrentView('home')}
          />
        );
      } else {
        return (
          <Signup 
            onSuccess={handleUserLogin}
            onSwitchToLogin={() => setAuthView('login')}
            onBack={() => setCurrentView('home')}
          />
        );
      }
    }

    // Admin Panel Routes
    if (currentView === 'admin-login') {
      return <AdminLogin onLogin={handleAdminLogin} onBack={() => setCurrentView('home')} />;
    }
    
    if (currentView === 'admin' && isAdminLoggedIn) {
      return <AdminDashboard onLogout={() => {
        setIsAdminLoggedIn(false);
        setCurrentView('home');
      }} />;
    }

    // Regular Routes
    switch (currentView) {
      case 'home':
        return <Homepage onExploreProducts={handleExploreProducts} />;
      case 'about':
        return <About />;
      case 'contact':
        return <Contact />;
      case 'products':
        return <ProductCatalog onProductSelect={handleProductSelect} />;
      case 'kit-designer':
        if (kitDesignData) {
          return (
            <OrderPlacement
              kitDesign={kitDesignData}
              product={{ name: 'Custom Kit — 3D Designer', price: '£35.00', image: '' }}
              user={user}
              onBack={() => setKitDesignData(null)}
            />
          );
        }
        return (
          <KitDesigner3D
            onBack={() => setCurrentView('home')}
            onRequestOrder={(designData) => setKitDesignData(designData)}
          />
        );
      case 'customize':
        if (!selectedProduct) {
          return <ProductCatalog onProductSelect={handleProductSelect} />;
        }
        if (showOrderPlacement) {
          return (
            <OrderPlacement 
              product={selectedProduct}
              customization={customization}
              user={user}
              onBack={() => {
                setShowOrderPlacement(false);
                setSelectedProduct(null);
                setCurrentView('products');
              }}
            />
          );
        }
        return <ProductCatalog onProductSelect={handleProductSelect} />;
      default:
        return <Homepage onExploreProducts={handleExploreProducts} />;
    }
  };

  return (
    <ThemeProvider>
      <div className="App">
        {/* Only show header if not in admin panel or auth */}
        {currentView !== 'admin' && currentView !== 'admin-login' && currentView !== 'auth' && (
          <Header currentView={currentView} onNavigate={handleNavigation} user={user} onLogout={handleUserLogout} />
        )}
        {renderCurrentView()}
      </div>
    </ThemeProvider>
  );
}

export default App;
